import { useWatch } from 'react-hook-form'

import { scopedTranslation } from '@utils/I18n'

import { SelectField } from '@components/molecules/SelectField'

import { type AvailableFilterPredicate, initialValues, findPredicate, requiresValue } from './stores/filterStoreSlice'
import { type FieldProps } from './PropertyField'

const t = scopedTranslation('components.filter_dialog')

// Switching predicate on the same property keeps the values the user has
// already entered when the new predicate takes the same shape of value (any of
// -> all of, before -> after, contains -> does not contain). Only a change of
// shape, or a predicate that carries its own fixed value, resets them.
const keepsValues = (previous: AvailableFilterPredicate | undefined, next: AvailableFilterPredicate | undefined) =>
  previous !== undefined && next !== undefined && requiresValue(previous) && requiresValue(next) && previous.array === next.array

const PredicateField = ({ control, index, filteringOptions, update }: FieldProps) => {
  const filter = useWatch({ control, name: `rules.${index}` })
  const { property, predicate } = filter
  const fieldName = `rules.${index}.predicate` as const
  const availablePredicates: AvailableFilterPredicate[] = property ? filteringOptions[property].predicates : []

  return (
    <SelectField
      className="tw:h-full"
      hideLabel
      name={fieldName}
      label={t('labels.predicate')}
      options={availablePredicates.map((predicate) => ({
        value: predicate.value,
        label: predicate.label,
      }))}
      onValueChange={(nextPredicate) => {
        const current = findPredicate(availablePredicates, predicate)
        const next = findPredicate(availablePredicates, nextPredicate)

        update(
          index,
          keepsValues(current, next)
            ? { ...filter, predicate: nextPredicate }
            : { ...filter, predicate: nextPredicate, values: initialValues(next) }
        )
      }}
      value={predicate}
    />
  )
}

export default PredicateField
